import { useState } from "react";
import { Calendar, Sparkles } from "lucide-react";
import DatePicker from "../../../components/DatePicker";

const GlobalEffectiveDateBar = ({
  globalDate,
  onDateChange,
  isRTL = false,
  theme = "light",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isDark = theme === "dark";

  // ─── Theme Colors ──────────────────────────────────────────────────────────
  const primary = {
    base: "#2e7cba",
    light: "#5da3da",
    dark: "#1a5b8e",
    glow: "rgba(46,124,186,0.18)",
  };

  const bg = {
    card: isDark ? "rgba(30,41,59,0.6)" : "rgba(255,255,255,0.95)",
    cardAlt: isDark ? "rgba(51,65,85,0.35)" : "rgba(248,250,252,0.9)",
    input: isDark ? "rgba(30,41,59,0.8)" : "#ffffff",
    border: isDark ? "rgba(148,163,184,0.12)" : "rgba(226,232,240,0.8)",
  };

  const textColor = {
    primary: isDark ? "#f1f5f9" : "#0f172a",
    secondary: isDark ? "rgba(226,232,240,0.7)" : "#64748b",
    muted: isDark ? "rgba(148,163,184,0.6)" : "#94a3b8",
  };

  const shadow = {
    sm: isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
    md: isDark ? "0 4px 16px rgba(0,0,0,0.35)" : "0 4px 16px rgba(0,0,0,0.06)",
    glow: isDark
      ? `0 0 0 1px ${primary.glow}, 0 8px 32px rgba(46,124,186,0.25)`
      : `0 0 0 1px ${primary.glow}, 0 4px 20px rgba(46,124,186,0.12)`,
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "24px",
        background: bg.card,
        borderRadius: "16px",
        border: `1px solid ${bg.border}`,
        display: "flex",
        alignItems: "center",
        gap: "24px",
        flexWrap: "wrap",
        boxShadow: isHovered ? shadow.glow : shadow.md,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >

      {/* Icon Container */}
      <div
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "14px",
          background: isDark
            ? `linear-gradient(135deg, ${primary.dark} 0%, ${primary.base} 100%)`
            : `linear-gradient(135deg, ${primary.light} 0%, ${primary.base} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: shadow.md,
          position: "relative",
          zIndex: 1,
          transform: isHovered ? "scale(1.05)" : "scale(1)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Calendar size={32} style={{ color: "#ffffff" }} />

        {/* Sparkle Icon Overlay */}
        <div
          style={{
            position: "absolute",
            top: "-4px",
            [isRTL ? "left" : "right"]: "-4px",
            background: isDark ? primary.light : "#ffffff",
            borderRadius: "50%",
            padding: "4px",
            boxShadow: shadow.sm,
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? "scale(1)" : "scale(0.8)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Sparkles size={12} style={{ color: primary.base }} />
        </div>
      </div>

      {/* Text Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          minWidth: "200px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label
            style={{
              fontSize: "17px",
              fontWeight: "700",
              color: textColor.primary,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            {isRTL ? "التاريخ الفعال العام" : "Global Effective Date"}
          </label>
          <div
            style={{
              padding: "2px 8px",
              borderRadius: "6px",
              background: isDark ? primary.glow : "rgba(46,124,186,0.1)",
              fontSize: "10px",
              fontWeight: "600",
              color: primary.light,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {isRTL ? "ذكي" : "Smart"}
          </div>
        </div>
        <p
          style={{
            fontSize: "13px",
            color: textColor.secondary,
            margin: 0,
            lineHeight: "1.5",
          }}
        >
          {isRTL
            ? "يُطبق على جميع الأقسام. فك القفل لتخصيص تاريخ معين."
            : "Applies to all tabs. Unlock any tab for custom dates."}
        </p>
      </div>

      {/* Date Picker with Enhanced Design */}
      <div
        style={{
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            position: "relative",
            display: "inline-block",
          }}
        >
          {/* Label Above Input */}
          <div
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: textColor.muted,
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              paddingLeft: isRTL ? "0" : "4px",
              paddingRight: isRTL ? "4px" : "0",
            }}
          >
            {isRTL ? "اختر التاريخ" : "Select Date"}
          </div>

          <div style={{ position: "relative" }}>
            <DatePicker
              theme={theme}
              isRTL={isRTL}
              value={globalDate || new Date().toISOString().split("T")[0]}
              onChange={(v) => onDateChange(v)}
              buttonStyle={{
                padding: "14px 18px",
                fontSize: "15px",
                fontWeight: "600",
                borderRadius: "12px",
                background: bg.input,
                color: textColor.primary,
                minWidth: "190px",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalEffectiveDateBar;
